import { useEffect, useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import {
  FiBook,
  FiClock,
  FiLayers,
  FiCalendar,
  FiGlobe,
  FiBarChart2,
  FiPlusCircle,
} from "react-icons/fi";

// Primary Color: #4096F6
const themeBlue = "#4096F6";

export default function ExamCreate() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    categoryId: "",
    testName: "",
    totalSections: "",
    totalQuestions: "",
    durationMinutes: "",
    testYear: "",
    negativeMarks: "",
    difficulty: "",
    language: "",
  });

  const getCategories = async () => {
    const res = await makeRequestAuth("post", "/admin/getCategoryList", {
      pageNo: 1,
      size: 100,
    });
    setCategories(res?.data?.list || []);
  };

  const validateForm = () => {
    if (!form.categoryId) return "Please select a category";
    if (!form.testName.trim()) return "Test name is required";
    if (!form.totalSections) return "Total sections count is required";
    if (!form.totalQuestions) return "Total questions count is required";
    if (!form.durationMinutes) return "Duration is required";
    if (!form.testYear) return "Test year is required";
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      showErrorToast(error);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...form,
        totalSections: Number(form.totalSections),
        totalQuestions: Number(form.totalQuestions),
        durationMinutes: Number(form.durationMinutes),
        negativeMarks: form.negativeMarks ? Number(form.negativeMarks) : null,
        difficulty: form.difficulty || null,
        language: form.language || null,
      };

      const res = await makeRequestAuth("post", "/admin/createExam", payload);
      if (res?.error) {
        showErrorToast(res.message || "Validation failed");
        return;
      }

      showSuccessToast("Exam created successfully!");
      setForm({
        categoryId: "",
        testName: "",
        totalSections: "",
        totalQuestions: "",
        durationMinutes: "",
        testYear: "",
        negativeMarks: "",
        difficulty: "",
        language: "",
      });
    } catch {
      showErrorToast("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  return (
    <div className="p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800">Create New Exam</h1>
          <p className="text-slate-500 mt-2">
            Setup exam parameters, duration, and difficulty settings.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-8">
            {/* SECTION 1: BASIC INFO */}
            <div className="flex items-center gap-2 mb-6 text-slate-400">
              <FiBook />
              <h2 className="text-sm font-bold uppercase tracking-widest">
                Basic Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700 ml-1">
                  Category
                </label>
                <select
                  className="w-full border border-slate-200 bg-slate-50/50 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#4096F6]/20 focus:border-[#4096F6] outline-none transition-all appearance-none cursor-pointer"
                  value={form.categoryId}
                  onChange={(e) =>
                    setForm({ ...form, categoryId: e.target.value })
                  }
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700 ml-1">
                  Exam Name
                </label>
                <input
                  placeholder="e.g. JEE Mains Mock Test"
                  className="w-full border border-slate-200 bg-slate-50/50 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#4096F6]/20 focus:border-[#4096F6] outline-none transition-all"
                  value={form.testName}
                  onChange={(e) =>
                    setForm({ ...form, testName: e.target.value })
                  }
                />
              </div>
            </div>

            {/* SECTION 2: EXAM STRUCTURE */}
            <div className="flex items-center gap-2 mb-6 text-slate-400">
              <FiLayers />
              <h2 className="text-sm font-bold uppercase tracking-widest">
                Exam Structure
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              <InputField
                label="Total Sections"
                type="number"
                value={form.totalSections}
                onChange={(v) => setForm({ ...form, totalSections: v })}
                icon={<FiLayers className="text-slate-400" />}
              />
              <InputField
                label="Total Questions"
                type="number"
                value={form.totalQuestions}
                onChange={(v) => setForm({ ...form, totalQuestions: v })}
                icon={<FiBarChart2 className="text-slate-400" />}
              />
              <InputField
                label="Duration (Min)"
                type="number"
                value={form.durationMinutes}
                onChange={(v) => setForm({ ...form, durationMinutes: v })}
                icon={<FiClock className="text-slate-400" />}
              />
            </div>

            {/* SECTION 3: ADDITIONAL SETTINGS */}
            <div className="flex items-center gap-2 mb-6 text-slate-400">
              <FiGlobe />
              <h2 className="text-sm font-bold uppercase tracking-widest">
                Advanced Settings
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700 ml-1">
                  Year
                </label>
                <select
                  className="w-full border border-slate-200 bg-slate-50/50 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#4096F6]/20 focus:border-[#4096F6] outline-none transition-all"
                  value={form.testYear}
                  onChange={(e) =>
                    setForm({ ...form, testYear: e.target.value })
                  }
                >
                  <option value="">Year</option>
                  {Array.from({ length: 10 }).map((_, i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <option key={y} value={String(y)}>
                        {y}
                      </option>
                    );
                  })}
                </select>
              </div>

              <InputField
                label="Negative Marks"
                type="number"
                placeholder="0.25"
                value={form.negativeMarks}
                onChange={(v) => setForm({ ...form, negativeMarks: v })}
              />

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700 ml-1">
                  Difficulty
                </label>
                <select
                  className="w-full border border-slate-200 bg-slate-50/50 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#4096F6]/20 focus:border-[#4096F6] outline-none transition-all"
                  value={form.difficulty}
                  onChange={(e) =>
                    setForm({ ...form, difficulty: e.target.value })
                  }
                >
                  <option value="">Difficulty</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700 ml-1">
                  Language
                </label>
                <select
                  className="w-full border border-slate-200 bg-slate-50/50 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#4096F6]/20 focus:border-[#4096F6] outline-none transition-all"
                  value={form.language}
                  onChange={(e) =>
                    setForm({ ...form, language: e.target.value })
                  }
                >
                  <option value="">Language</option>
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                </select>
              </div>
            </div>
          </div>

          {/* Footer Action */}
          <div className="bg-slate-50/80 px-8 py-5 flex items-center justify-between border-t border-slate-100">
            <p className="text-xs text-slate-400 italic font-medium">
              * Ensure all required fields are filled
            </p>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`px-10 py-3 rounded-2xl text-white font-bold flex items-center gap-3 transition-all active:scale-95 shadow-lg shadow-blue-500/20 hover:brightness-110 ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
              style={{ backgroundColor: themeBlue }}
            >
              {loading ? (
                <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FiPlusCircle size={20} />
              )}
              {loading ? "Processing..." : "Create Exam Instance"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* HELPER COMPONENT FOR CLEANER CODE */
function InputField({ label, type, value, onChange, placeholder, icon }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-slate-700 ml-1">
        {label}
      </label>
      <div className="relative">
        <input
          type={type}
          placeholder={placeholder || label}
          className="w-full border border-slate-200 bg-slate-50/50 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#4096F6]/20 focus:border-[#4096F6] outline-none transition-all"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}
